import React, { useState, useEffect } from 'react';
import { MessageCircle, Send, CheckCircle, AlertCircle, Lock } from 'lucide-react';
import api from '../api';
import PlanModal from '../components/PlanModal';
import './Messaggi.css';

function Messaggi() {
  const [messaggi, setMessaggi] = useState([]);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [replyStatus, setReplyStatus] = useState('idle');
  const [bloccato, setBloccato] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);

  useEffect(() => {
    loadMessaggi();
  }, []);

  const loadMessaggi = async () => {
    try {
      const res = await api.get('/messaggi');
      setMessaggi(res.data.messaggi || res.data || []);
      setBloccato(!!res.data.bloccato);
    } catch (err) {
      setError('Errore nel caricamento dei messaggi');
      console.error('Error loading messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleMessageDetail = async (msg) => {
    if (selectedMsg?.id === msg.id) {
      setSelectedMsg(null);
    } else {
      setSelectedMsg(msg);
      // Mark as read
      if (!msg.letto) {
        try {
          await api.patch(`/messaggi/${msg.id}`, { letto: true });
          setMessaggi((prev) =>
            prev.map((m) =>
              m.id === msg.id ? { ...m, letto: true } : m
            )
          );
        } catch (err) {
          console.error('Error marking message as read:', err);
        }
      }
    }
  };

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedMsg) return;

    setReplyStatus('loading');
    try {
      await api.patch(`/messaggi/${selectedMsg.id}`, {
        risposta: replyText,
      });

      setReplyText('');
      setReplyStatus('success');

      // Reload messages
      await loadMessaggi();
      setSelectedMsg(null);

      setTimeout(() => setReplyStatus('idle'), 3000);
    } catch (err) {
      setReplyStatus('error');
      console.error('Error sending reply:', err);
    }
  };

  const unreadCount = messaggi.filter((m) => !m.letto).length;

  if (loading) {
    return (
      <div className="messaggi-page loading">
        <div className="spinner" />
        Caricamento messaggi...
      </div>
    );
  }

  return (
    <div className="messaggi-page">
      <div className="messaggi-container">
        {/* Header */}
        <div className="messaggi-header">
          <div>
            <h1>
              <MessageCircle size={32} />
              Messaggi
            </h1>
            {unreadCount > 0 && (
              <p className="unread-info">{unreadCount} messaggi non letti</p>
            )}
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        {bloccato ? (
          <div className="messaggi-locked">
            <div className="locked-icon"><Lock size={40} /></div>
            <h2>{messaggi.length > 0 ? `Hai ${messaggi.length} ${messaggi.length === 1 ? 'messaggio' : 'messaggi'} in attesa` : 'I messaggi sono una funzione Pro'}</h2>
            <p>
              Con il piano gratuito puoi ricevere richieste ma non leggerle.
              Passa a <strong>Pro</strong> per leggere e rispondere ai genitori interessati.
            </p>
            <button className="btn btn-primary" onClick={() => setPlanOpen(true)}>
              Passa a Pro
            </button>
            {messaggi.length > 0 && (
              <ul className="locked-list">
                {messaggi.map((m) => (
                  <li key={m.id}>
                    <Lock size={14} />
                    <span>Messaggio bloccato</span>
                    <span className="locked-date">{new Date(m.created_at).toLocaleDateString('it-IT')}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
        <div className="messaggi-content">
          {/* Messages List */}
          <div className="messaggi-list">
            {messaggi.length > 0 ? (
              messaggi.map((msg) => (
                <div
                  key={msg.id}
                  className={`message-item ${!msg.letto ? 'unread' : ''} ${
                    selectedMsg?.id === msg.id ? 'selected' : ''
                  }`}
                  onClick={() => toggleMessageDetail(msg)}
                >
                  <div className="message-item-header">
                    <div className="message-sender">
                      <strong>{msg.mittente_nome}</strong>
                      {!msg.letto && <span className="unread-dot" />}
                    </div>
                    <span className="message-date">
                      {new Date(msg.created_at).toLocaleDateString('it-IT')}
                    </span>
                  </div>
                  <div className="message-item-body">
                    <p className="message-subject">{msg.oggetto}</p>
                    <p className="message-preview">
                      {(msg.messaggio || '').substring(0, 80)}
                      {(msg.messaggio || '').length > 80 ? '...' : ''}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <MessageCircle size={48} />
                <p>Nessun messaggio</p>
              </div>
            )}
          </div>

          {/* Message Detail */}
          {selectedMsg && (
            <div className="message-detail">
              <div className="detail-header">
                <div>
                  <h3>{selectedMsg.mittente_nome}</h3>
                  <p className="detail-date">
                    {new Date(selectedMsg.created_at).toLocaleDateString('it-IT', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                </div>
              </div>

              <div className="detail-body">
                <p className="detail-subject">
                  <strong>Oggetto:</strong> {selectedMsg.oggetto}
                </p>

                {selectedMsg.mittente_email && (
                  <p className="detail-email">
                    <strong>Email:</strong>{' '}
                    <a href={`mailto:${selectedMsg.mittente_email}`}>{selectedMsg.mittente_email}</a>
                  </p>
                )}

                {selectedMsg.mittente_telefono && (
                  <p className="detail-phone">
                    <strong>Telefono:</strong>{' '}
                    <a href={`tel:${selectedMsg.mittente_telefono}`}>{selectedMsg.mittente_telefono}</a>
                  </p>
                )}

                <div className="detail-message">
                  <p>{selectedMsg.messaggio}</p>
                </div>

                {selectedMsg.risposta && (
                  <div className="detail-service">
                    <strong>La tua risposta:</strong> {selectedMsg.risposta}
                  </div>
                )}
              </div>

              {/* Reply Form */}
              <form onSubmit={handleReplySubmit} className="reply-form">
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Scrivi la tua risposta..."
                  rows="4"
                />

                {replyStatus === 'success' && (
                  <div className="status-message success">
                    <CheckCircle size={18} />
                    <span>Risposta inviata con successo!</span>
                  </div>
                )}

                {replyStatus === 'error' && (
                  <div className="status-message error">
                    <AlertCircle size={18} />
                    <span>Errore nell'invio della risposta</span>
                  </div>
                )}

                <button
                  type="submit"
                  className="btn btn-reply"
                  disabled={!replyText.trim() || replyStatus === 'loading'}
                >
                  {replyStatus === 'loading' ? (
                    <>
                      <span className="spinner-small" />
                      Invio in corso...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Invia Risposta
                    </>
                  )}
                </button>
              </form>
            </div>
          )}
        </div>
        )}
      </div>
      <PlanModal open={planOpen} onClose={() => setPlanOpen(false)} />
    </div>
  );
}

export default Messaggi;
